import { useEffect, useRef, useState } from "react";
import { XIcon } from "@heroicons/react/outline";

const getInitialForm = (eventId) => ({
  event: eventId,
  name: "",
  type: "online",
  price: "",
  currency: "EUR",
  series: "",
  quantity_total: "",
  allow_multi_ticket_per_order: false,
  tickets_per_order: 1,
  commission: "client",
  //commission_percentage: "",
  is_display_full_price: true,
  conditional_display_enabled: false,
  sale_start: "",
  sale_end: "",
  display_before_sale: false,
  pre_sale_display_mode: "countdown",
  conditional_access_enabled: false,
  is_display_on_sold_out: false,
  display_after: "",
  access_from: "",
  access_until: "",
  description: "",
  is_display_total_available_tickets: false,
});

const TicketFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  mode = "create",
  initialData = null,
  eventId,
  ticketTypes,
}) => {
  const [form, setForm] = useState(getInitialForm(eventId));

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (mode === "edit" && initialData) {
      setForm({
        ...initialData,
        sale_start: initialData.sale_start?.slice(0, 16) || "",
        sale_end: initialData.sale_end?.slice(0, 16) || "",
        access_from: initialData.access_from?.slice(0, 16) || "",
        access_until: initialData.access_until?.slice(0, 16) || "",
      });
    }
  }, [mode, initialData]);

  useEffect(() => {
    if (!isOpen) {
      setForm(getInitialForm(eventId));
    }
  }, [isOpen, eventId]);

  if (!isOpen) return null;

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const formatDateTime = (value) => {
    if (!value) return null;
    return value.length === 16 ? `${value}:00` : value;
  };

  const handleSubmit = () => {
    const payload = {
      ...form,
      sale_start: formatDateTime(form.sale_start),
      sale_end: formatDateTime(form.sale_end),
      access_from: formatDateTime(form.access_from),
      access_until: formatDateTime(form.access_until),

      quantity_total: Number(form.quantity_total),
      tickets_per_order: Number(form.tickets_per_order),
      //commission_percentage: Number(form.commission_percentage),
      price: String(form.price),
    };

    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-3xl bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        {/* HEADER */}
        <div className="flex justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-white font-semibold">
            {mode === "edit" ? "Edit Ticket" : "Add Ticket"}
          </h2>

          <button onClick={onClose}>
            <XIcon className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* BODY */}
        <div className="px-6 py-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* BASIC INFO */}
          <div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm text-slate-300 mb-1">Category Name</h3>
                <input
                  placeholder="Enter Name"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                />
              </div>

              <div>
                <h3 className="text-sm text-slate-300 mb-1">Select Type</h3>
                <select
                  value={form.type}
                  onChange={(e) => handleChange("type", e.target.value)}
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                >
                  <option value="online">Online</option>
                  <option value="terminal">Terminal</option>
                </select>
              </div>
            </div>
          </div>

          {/* PRICING */}
          <div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm text-slate-300 mb-1">Price</h3>
                <input
                  type="number"
                  placeholder="Price"
                  value={form.price}
                  onChange={(e) => handleChange("price", e.target.value)}
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                />
              </div>

              <div>
                <h3 className="text-sm text-slate-300 mb-1">Select Currency</h3>
                <select
                  value={form.currency}
                  onChange={(e) => handleChange("currency", e.target.value)}
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                >
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="RON">RON</option>
                  <option value="AED">AED</option>
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm text-slate-300 mb-1">Series</h3>
              <input
                placeholder="Enter Series"
                value={form.series}
                onChange={(e) => handleChange("series", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <h3 className="text-sm text-slate-300 mb-1">Quantity Total</h3>
              <input
                placeholder="Enter Total"
                value={form.quantity_total}
                onChange={(e) => handleChange("quantity_total", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>

          <div className="border-t border-white/10 my-4" />

          <div>
            <label className="text-sm text-slate-300 mb-1">
              I want more than one ticket to be issued when purchasing this
              ticket type:
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  className="accent-pink-600"
                  checked={form.allow_multi_ticket_per_order === true}
                  onChange={() =>
                    handleChange("allow_multi_ticket_per_order", true)
                  }
                />
                Yes
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  className="accent-pink-600"
                  checked={form.allow_multi_ticket_per_order === false}
                  onChange={() =>
                    handleChange("allow_multi_ticket_per_order", false)
                  }
                />
                No
              </label>
            </div>
          </div>

          {form.allow_multi_ticket_per_order && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm text-slate-300 mb-1">
                  Tickets Issued per order
                </h3>
                <input
                  placeholder="Enter tickets issued per order"
                  type="number"
                  value={form.tickets_per_order}
                  onChange={(e) =>
                    handleChange("tickets_per_order", e.target.value)
                  }
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                />
              </div>
            </div>
          )}

          <div className="border-t border-white/10 my-4" />

          {/* COMMISSION */}
          <div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm text-slate-300 mb-1">Commission</h3>
                <select
                  value={form.commission}
                  onChange={(e) => handleChange("commission", e.target.value)}
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                >
                  <option value="client">Client</option>
                  <option value="seller">Seller</option>
                </select>
              </div>
              {/* 
              <div>
                <h3 className="text-sm text-slate-300 mb-1">
                  Commission Percentage
                </h3>
                <input
                  type="number"
                  placeholder="Enter percentage %"
                  value={form.commission_percentage}
                  onChange={(e) =>
                    handleChange("commission_percentage", e.target.value)
                  }
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                />
              </div>*/}
            </div>
          </div>

          <div className="border-t border-white/10 my-4" />

          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="text-sm text-slate-300">
                Display full price:
              </label>
              <div className="relative group cursor-pointer">
                <span className="text-xs text-pink-700 border border-pink-700 rounded-full w-4 h-4 flex items-center justify-center">
                  i
                </span>
                <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-56 bg-slate-800 text-xs text-white p-2 rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition z-50">
                  By activating this option, customer will see ticket price +
                  any commissions. If you would like for customer to see ticket
                  price, and commission to be displayed onto the pay step,
                  select NO.
                </div>
              </div>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  className="accent-pink-600"
                  checked={form.is_display_full_price === true}
                  onChange={() => handleChange("is_display_full_price", true)}
                />
                Yes
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  className="accent-pink-600"
                  checked={form.is_display_full_price === false}
                  onChange={() => handleChange("is_display_full_price", false)}
                />
                No
              </label>
            </div>
          </div>

          <div className="border-t border-white/10 my-4" />

          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="text-sm text-slate-300">
                Conditional Display Enabled:
              </label>
              <div className="relative group cursor-pointer">
                <span className="text-xs text-pink-700 border border-pink-700 rounded-full w-4 h-4 flex items-center justify-center">
                  i
                </span>
                <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-56 bg-slate-800 text-xs text-white p-2 rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition z-50">
                  By using this option, tickets will only be displayed on the
                  website between the selected dates/times. Use this option for
                  multiple stages sales.
                </div>
              </div>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  className="accent-pink-600"
                  checked={form.conditional_display_enabled === true}
                  onChange={() =>
                    handleChange("conditional_display_enabled", true)
                  }
                />
                Yes
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  className="accent-pink-600"
                  checked={form.conditional_display_enabled === false}
                  onChange={() =>
                    handleChange("conditional_display_enabled", false)
                  }
                />
                No
              </label>
            </div>
          </div>

          {form.conditional_display_enabled === true && (
            <div>
              <label className="text-sm text-slate-300 mb-1">
                Display when a ticket is sold out:
              </label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    className="accent-pink-600"
                    checked={form.is_display_on_sold_out === true}
                    onChange={() =>
                      handleChange("is_display_on_sold_out", true)
                    }
                  />
                  Yes
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    className="accent-pink-600"
                    checked={form.is_display_on_sold_out === false}
                    onChange={() =>
                      handleChange("is_display_on_sold_out", false)
                    }
                  />
                  No
                </label>
              </div>
            </div>
          )}

          {form.conditional_display_enabled === true &&
            form.is_display_on_sold_out === true && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-1">
                    Which ticket type must sellout before this one goes live:
                  </label>
                  <div className="relative" ref={dropdownRef}>
                    {/* Trigger */}
                    <button
                      type="button"
                      disabled={!ticketTypes.length}
                      onClick={() => setDropdownOpen((prev) => !prev)}
                      className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40 flex items-center justify-between disabled:opacity-50"
                    >
                      <span
                        className={
                          form.display_after ? "text-white" : "text-slate-400"
                        }
                      >
                        {form.display_after
                          ? ticketTypes.find((t) => t.id === form.display_after)
                              ?.name
                          : "Select"}
                      </span>
                      <svg
                        className={`w-4 h-4 text-slate-400 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {/* Dropdown list */}
                    {dropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-[#131C31] border border-white/10 rounded-lg overflow-hidden">
                        <ul className="max-h-48 overflow-y-auto py-1">
                          <li
                            onClick={() => {
                              handleChange("display_after", null);
                              setDropdownOpen(false);
                            }}
                            className="px-4 py-2 text-sm text-slate-400 hover:bg-white/5 cursor-pointer"
                          >
                            Select
                          </li>
                          {ticketTypes
                            .filter((t) => t.id !== initialData?.id)
                            .map((ticketType) => (
                              <li
                                key={ticketType.id}
                                onClick={() => {
                                  handleChange("display_after", ticketType.id);
                                  setDropdownOpen(false);
                                }}
                                className={`px-4 py-2 text-sm cursor-pointer hover:bg-white/5 transition
                ${form.display_after === ticketType.id ? "text-pink-400 bg-white/5" : "text-white"}`}
                              >
                                {ticketType.name}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

          {/* SALE SETTINGS */}
          {form.conditional_display_enabled === true &&
            form.is_display_on_sold_out === false && (
              <div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm text-slate-300 mb-2">
                      Sale Start Date & Time
                    </h3>
                    <input
                      type="datetime-local"
                      value={form.sale_start}
                      onChange={(e) =>
                        handleChange("sale_start", e.target.value)
                      }
                      className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                    />
                  </div>

                  <div>
                    <h3 className="text-sm text-slate-300 mb-2">
                      Sale End Date & Time
                    </h3>
                    <input
                      type="datetime-local"
                      value={form.sale_end}
                      onChange={(e) => handleChange("sale_end", e.target.value)}
                      className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                    />
                  </div>
                </div>
              </div>
            )}

          {form.conditional_display_enabled === true && (
            <div>
              <label className="text-sm text-slate-300 mb-1">
                Display before sale:
              </label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    className="accent-pink-600"
                    checked={form.display_before_sale === true}
                    onChange={() => handleChange("display_before_sale", true)}
                  />
                  Yes
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    className="accent-pink-600"
                    checked={form.display_before_sale === false}
                    onChange={() => handleChange("display_before_sale", false)}
                  />
                  No
                </label>
              </div>
            </div>
          )}

          {form.display_before_sale === true &&
            form.conditional_display_enabled === true && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm text-slate-300 mb-1">
                    Pre Sale Display Mode
                  </h3>

                  <select
                    value={form.pre_sale_display_mode}
                    onChange={(e) =>
                      handleChange("pre_sale_display_mode", e.target.value)
                    }
                    className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                  >
                    <option value="countdown">Countdown</option>
                    <option value="display_start_selling_date">
                      Display Start Selling Date
                    </option>
                  </select>
                </div>
              </div>
            )}

          <div className="border-t border-white/10 my-4" />

          <div>
            <label className="text-sm text-slate-300 mb-1">
              Conditional Access Enabled:
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  className="accent-pink-600"
                  checked={form.conditional_access_enabled === true}
                  onChange={() =>
                    handleChange("conditional_access_enabled", true)
                  }
                />
                Yes
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  className="accent-pink-600"
                  checked={form.conditional_access_enabled === false}
                  onChange={() =>
                    handleChange("conditional_access_enabled", false)
                  }
                />
                No
              </label>
            </div>
          </div>

          {/* ACCESS */}
          {form.conditional_access_enabled === true && (
            <div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm text-slate-300 mb-2">Access From</h3>
                  <input
                    type="datetime-local"
                    value={form.access_from}
                    onChange={(e) =>
                      handleChange("access_from", e.target.value)
                    }
                    className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                  />
                </div>

                <div>
                  <h3 className="text-sm text-slate-300 mb-2">Access Until</h3>
                  <input
                    type="datetime-local"
                    value={form.access_until}
                    onChange={(e) =>
                      handleChange("access_until", e.target.value)
                    }
                    className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-white/10 my-4" />

          {/* DESCRIPTION */}
          <div>
            <h3 className="text-sm text-slate-300 mb-2">Description</h3>
            <textarea
              placeholder="Ticket description"
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40min-h-[100px]"
            />
          </div>

          <div>
            <label className="text-sm text-slate-300 mb-1">
              Display total available tickets:
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  className="accent-pink-600"
                  checked={form.is_display_total_available_tickets === true}
                  onChange={() =>
                    handleChange("is_display_total_available_tickets", true)
                  }
                />
                Yes
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  className="accent-pink-600"
                  checked={form.is_display_total_available_tickets === false}
                  onChange={() =>
                    handleChange("is_display_total_available_tickets", false)
                  }
                />
                No
              </label>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="text-slate-400">
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            className="bg-pink-600 px-4 py-2 rounded text-white"
          >
            {mode === "edit" ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketFormModal;
